import { useCallback, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  Plus,
  Search,
  SquarePen,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { IncidentPosition } from 'hooks/incident/useRoster';
import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import EditPositionModal from 'components/IncidentConfig/EditPositionModal';
import { getSectionTheme } from 'components/IncidentConfig/rosterOrgChartTheme';
import AddChildPositionDialog from './AddChildPositionDialog';
import AddResourceToRosterDialog from './AddResourceToRosterDialog';
import { RosterFilterMenu } from './RosterFilterMenu';
import { SeatActivationRequestControl } from './SeatActivationRequestControl';
import type { RosterFilters } from './rosterPositionFilter';
import type { HwcgPeriodChoice } from './rosterPeriods';
import {
  buildRosterListTree,
  type RosterListNode,
  type RosterListOccupant,
} from './rosterListTree';

const openStorageKey = (incidentId: string) => `roster-list-open:${incidentId}`;

function readOpenIds(incidentId: string): string[] | null {
  try {
    const raw = window.sessionStorage.getItem(openStorageKey(incidentId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : null;
  } catch {
    return null;
  }
}

function persistOpenIds(incidentId: string, ids: string[]) {
  try {
    window.sessionStorage.setItem(
      openStorageKey(incidentId),
      JSON.stringify(ids)
    );
  } catch {
    // Private mode: open state just won't survive a reload.
  }
}

function memberName(member: RosterEntry): string {
  return (
    member.userName ||
    member.profileSettings?.userName ||
    member.emailAddress ||
    'Unnamed member'
  );
}

type SeatActions = {
  canEdit: (positionId: string) => boolean;
  onOpenSeat: (positionId: string) => void;
  onAddPosition: (positionId: string) => void;
  onAddResource: (positionId: string) => void;
};

type ListContext = SeatActions & {
  isOpen: (positionId: string) => boolean;
  setOpen: (positionId: string, open: boolean) => void;
  posById: Map<string, IncidentPosition>;
  incidentId: string;
  groupId: string;
  plannedPeriodId: string | null;
};

const OccupantRow = ({
  occupant,
  positionId,
  context,
}: {
  occupant: RosterListOccupant;
  positionId: string;
  context: ListContext;
}) => (
  <li className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-border tw:bg-muted/30 tw:px-2.5 tw:py-2">
    <div className="tw:min-w-0">
      <div className="tw:truncate tw:text-sm tw:font-medium">
        {memberName(occupant.member)}
      </div>
      {occupant.member.emailAddress ? (
        <div className="tw:truncate tw:text-xs tw:text-muted-foreground">
          {occupant.member.emailAddress}
        </div>
      ) : null}
    </div>
    <div className="tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:gap-2">
      {occupant.periodLabel ? (
        <Badge variant="outline">{occupant.periodLabel}</Badge>
      ) : null}
      {!occupant.later ? (
        <SeatActivationRequestControl
          incidentId={context.incidentId}
          groupId={context.groupId}
          positionId={positionId}
          member={occupant.member}
          plannedPeriodId={context.plannedPeriodId}
        />
      ) : null}
    </div>
  </li>
);

const RosterListSeatCard = ({
  node,
  context,
}: {
  node: RosterListNode;
  context: ListContext;
}) => {
  const { position } = node;
  const open = context.isOpen(position.id);
  const toggle = () => context.setOpen(position.id, !open);
  const theme = getSectionTheme(position.id, context.posById);
  const editable = context.canEdit(position.id);
  const assigned = node.occupants.length;

  return (
    <Card
      size="sm"
      className="dark tw:mt-2 tw:gap-0 tw:border-border tw:bg-card tw:py-0 tw:text-card-foreground tw:first:mt-0"
      data-open={open ? 'true' : undefined}
    >
      <Collapsible
        open={open}
        onOpenChange={(next) => context.setOpen(position.id, next)}
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          className="tw:flex tw:cursor-pointer tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:transition-colors tw:hover:bg-muted/40"
          onClick={toggle}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggle();
            }
          }}
        >
          <ChevronDown
            className={cn(
              'tw:h-4 tw:w-4 tw:shrink-0 tw:text-muted-foreground tw:transition-transform',
              open && 'tw:rotate-180'
            )}
          />
          <span
            aria-hidden
            className="tw:size-2.5 tw:shrink-0 tw:rounded-full tw:border"
            style={{ backgroundColor: theme.border, borderColor: theme.border }}
          />
          <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-medium">
            {position.name}
          </span>
          {position.isResourcePosition ? (
            <Badge variant="outline" className="tw:shrink-0">
              Resource
            </Badge>
          ) : assigned > 0 ? (
            <Badge variant="secondary" className="tw:shrink-0">
              {assigned} assigned
            </Badge>
          ) : (
            <Badge variant="destructive" className="tw:shrink-0">
              Unassigned
            </Badge>
          )}
          {node.descendantCount > 0 ? (
            <span className="tw:shrink-0 tw:text-xs tw:text-muted-foreground">
              +{node.descendantCount} seat
              {node.descendantCount === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
        <CollapsibleContent className="tw:min-w-0 tw:overflow-hidden">
          <div className="tw:space-y-2 tw:border-t tw:border-border tw:p-3">
            {editable ? (
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => context.onOpenSeat(position.id)}
                >
                  <SquarePen className="tw:h-4 tw:w-4" />
                  Open seat
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => context.onAddPosition(position.id)}
                >
                  <Plus className="tw:h-4 tw:w-4" />
                  Add position
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => context.onAddResource(position.id)}
                >
                  <Plus className="tw:h-4 tw:w-4" />
                  Add resource
                </Button>
              </div>
            ) : null}
            {assigned > 0 ? (
              <ul
                className="tw:space-y-2"
                aria-label={`People in ${position.name}`}
              >
                {node.occupants.map((occupant) => (
                  <OccupantRow
                    key={occupant.member.userId}
                    occupant={occupant}
                    positionId={position.id}
                    context={context}
                  />
                ))}
              </ul>
            ) : !position.isResourcePosition ? (
              <div className="tw:text-xs tw:text-muted-foreground">
                No one is assigned to this seat.
              </div>
            ) : null}
            {node.children.map((child) => (
              <RosterListSeatCard
                key={child.position.id}
                node={child}
                context={context}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

type RosterListViewProps = {
  incidentId: string;
  groupId: string;
  positions: IncidentPosition[];
  /** People on the roster being viewed (current, or the chosen period). */
  shown: RosterEntry[];
  /** People seated only in later periods ("All" view). */
  later?: RosterEntry[];
  periodLabels?: Record<string, string>;
  /** Draft roster, handed to the seat modal like the org chart does. */
  draftMembers: RosterEntry[] | null;
  /** The planned period being viewed, for seat activation requests. */
  plannedPeriodId: string | null;
  filters: RosterFilters;
  onFiltersChange: (filters: RosterFilters) => void;
  periodChoices: HwcgPeriodChoice[];
  /** Null means every seat is editable. */
  editablePositionIds: string[] | null;
};

/**
 * HWCG: the roster as nested expandable seat cards, the same information
 * architecture as the AOR list.
 */
export function RosterListView({
  incidentId,
  groupId,
  positions,
  shown,
  later = [],
  periodLabels = {},
  draftMembers,
  plannedPeriodId,
  filters,
  onFiltersChange,
  periodChoices,
  editablePositionIds,
}: RosterListViewProps) {
  const tree = useMemo(
    () =>
      buildRosterListTree({
        positions,
        shown,
        later,
        labels: periodLabels,
        filters,
      }),
    [filters, later, periodLabels, positions, shown]
  );
  const posById = useMemo(
    () => new Map(positions.map((position) => [position.id, position])),
    [positions]
  );
  const [storedOpen, setStoredOpen] = useState<string[] | null>(() =>
    readOpenIds(incidentId)
  );
  const openIds = useMemo(
    () =>
      new Set([
        ...(storedOpen ?? tree.roots.map((node) => node.position.id)),
        ...tree.autoOpenIds,
      ]),
    [storedOpen, tree]
  );
  const writeOpen = useCallback(
    (ids: string[]) => {
      setStoredOpen(ids);
      persistOpenIds(incidentId, ids);
    },
    [incidentId]
  );
  const setOpen = useCallback(
    (positionId: string, open: boolean) => {
      const next = new Set(openIds);
      if (open) next.add(positionId);
      else next.delete(positionId);
      writeOpen([...next]);
    },
    [openIds, writeOpen]
  );

  const [seatModalId, setSeatModalId] = useState<string | null>(null);
  const [addPositionParent, setAddPositionParent] = useState<string | null>(
    null
  );
  const [addResourceParent, setAddResourceParent] = useState<string | null>(
    null
  );

  const context: ListContext = {
    isOpen: (positionId) => openIds.has(positionId),
    setOpen,
    posById,
    incidentId,
    groupId,
    plannedPeriodId,
    canEdit: (positionId) =>
      editablePositionIds == null || editablePositionIds.includes(positionId),
    onOpenSeat: setSeatModalId,
    onAddPosition: setAddPositionParent,
    onAddResource: setAddResourceParent,
  };

  return (
    <div
      className="dark form-block tw:text-foreground"
      data-tour="roster-list-view"
    >
      <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <div className="tw:relative tw:min-w-[12rem] tw:flex-1">
          <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2.5 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            type="search"
            value={filters.search}
            onChange={(event) =>
              onFiltersChange({ ...filters, search: event.target.value })
            }
            placeholder="Search seats or people"
            aria-label="Search roster seats and people"
            className="tw:h-8 tw:pl-8 tw:text-xs"
          />
        </div>
        <RosterFilterMenu
          filters={filters}
          onFiltersChange={onFiltersChange}
          positions={positions}
          hwcgPeriodChoices={periodChoices}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => writeOpen(tree.allIds)}
        >
          <ChevronsUpDown className="tw:h-4 tw:w-4" />
          Expand all
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => writeOpen([])}
        >
          <ChevronsDownUp className="tw:h-4 tw:w-4" />
          Collapse all
        </Button>
      </div>

      {tree.roots.length === 0 ? (
        <p className="tw:rounded-md tw:border tw:border-border tw:bg-card tw:p-4 tw:text-sm tw:text-muted-foreground">
          {positions.length === 0
            ? 'This roster has no positions yet.'
            : 'No seats match these filters.'}
        </p>
      ) : (
        <div>
          {tree.roots.map((node) => (
            <RosterListSeatCard
              key={node.position.id}
              node={node}
              context={context}
            />
          ))}
        </div>
      )}

      {seatModalId ? (
        <EditPositionModal
          show
          onHide={() => setSeatModalId(null)}
          positionId={seatModalId}
          activeMembers={shown}
          draftMembers={draftMembers}
        />
      ) : null}
      {addPositionParent ? (
        <AddChildPositionDialog
          key={addPositionParent}
          show
          parentPositionId={addPositionParent}
          onClose={() => setAddPositionParent(null)}
        />
      ) : null}
      {addResourceParent ? (
        <AddResourceToRosterDialog
          key={addResourceParent}
          show
          defaultReportsToPositionId={addResourceParent}
          onClose={() => setAddResourceParent(null)}
        />
      ) : null}
    </div>
  );
}
